import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Center, Paper, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { Wrench } from "lucide-react";

import { coreApi } from "@/lib/api/client";
import { formatUntil, setMaintenance, useMaintenance, type MaintenanceInfo } from "@/lib/maintenance";

function MaintenancePage({ info }: { info: MaintenanceInfo }) {
  // /api/core/version/ stays reachable in maintenance: poll it to know when it is over.
  const { data, refetch, isFetching } = useQuery({
    queryKey: ["maintenance-recheck"],
    queryFn: ({ signal }) => coreApi.getVersion(signal).then((r) => r.data),
    refetchInterval: 30_000,
  });
  React.useEffect(() => {
    if (data?.maintenance && !data.maintenance.enabled) {
      setMaintenance(null);
      window.location.reload();
    }
  }, [data?.maintenance]);

  const until = formatUntil(info.until);
  return (
    <Center mih="100vh" p="md">
      <Paper withBorder radius="md" p="xl" maw={480} w="100%">
        <Stack align="center" gap="md" ta="center">
          <ThemeIcon size={56} radius="xl" variant="light">
            <Wrench size={28} />
          </ThemeIcon>
          <Title order={2}>Maintenance</Title>
          <Text>{info.message || "nodus is being updated and will be back in a few minutes."}</Text>
          {until && (
            <Text c="dimmed" fz="sm">
              Expected back at {until}.
            </Text>
          )}
          <Button variant="light" loading={isFetching} onClick={() => refetch()}>
            Try again
          </Button>
        </Stack>
      </Paper>
    </Center>
  );
}

/** The portal, or — once the API answered 503 maintenance (#459) — a full-page maintenance screen. */
export function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const info = useMaintenance();
  if (info) return <MaintenancePage info={info} />;
  return <>{children}</>;
}
